import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { http } from "../../utils/httpCommon";
import Table from "../DataGrid/Table";
import LoadingCircle from "../LoadingCircle/LoadingCircle";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "bookings", label: "Bookings" },
  { key: "day_passes", label: "Day Passes" },
  { key: "offices", label: "Offices" },
  { key: "vas", label: "VAS" },
  { key: "credit", label: "Credit" },
  { key: "packages", label: "Packages" },
  { key: "changes", label: "Changes" },
];

const SERVICE_TYPE_LABELS = {
  booking: "Booking",
  check_in: "Check In",
  office_rental: "Office Rental",
  vas: "VAS",
  credit_purchase: "Credit Purchase",
  credit_redemption: "Credit Redemption",
  package_purchase: "Package Purchase",
  package_redemption: "Package Redemption",
  change_edit: "Change / Edit",
};

const formatServiceType = (value) => SERVICE_TYPE_LABELS[value] || value || "—";

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toFixed(2);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return dayjs(value).format("MMM D, YYYY h:mm A");
};

const ActivityLogs = ({ entityType, entityId }) => {
  const [filter, setFilter] = useState("all");
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  const basePath = entityType === "company" ? "/company" : "/client";

  useEffect(() => {
    if (!entityId) return;

    const loadActivityLog = async () => {
      setPageState((old) => ({ ...old, isLoading: true }));

      try {
        const res = await http.get(
          `${basePath}/${entityId}/activity-log?page=${pageState.page}&limit=${pageState.pageSize}&filter=${filter}`
        );

        const payload = res?.data?.data || {};
        const rows = (payload.items || []).map((item) => ({
          id: item.id,
          ...item,
          dateTimeDisplay: formatDateTime(item.dateTime),
          serviceTypeDisplay: formatServiceType(item.serviceType),
          amountPaidDisplay: formatAmount(item.amountPaid),
        }));

        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: rows,
          total: payload.total || 0,
        }));
      } catch (error) {
        console.error(error);
        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: [],
          total: 0,
        }));
      }
    };

    loadActivityLog();
  }, [basePath, entityId, filter, pageState.page, pageState.pageSize]);

  const columns = [
    {
      field: "dateTimeDisplay",
      headerName: "Date & Time",
      width: 180,
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 110,
      valueFormatter: (params) => params.value || "—",
    },
    {
      field: "facilityName",
      headerName: "Facility",
      width: 160,
      valueFormatter: (params) => params.value || "—",
    },
    {
      field: "serviceTypeDisplay",
      headerName: "Service Type",
      width: 160,
    },
    {
      field: "amountPaidDisplay",
      headerName: "Amount Paid",
      width: 120,
    },
    {
      field: "paymentMethod",
      headerName: "Payment Method",
      width: 150,
      valueFormatter: (params) => params.value || "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      valueFormatter: (params) => params.value || "—",
    },
    {
      field: "actor",
      headerName: "Actor",
      width: 140,
      valueFormatter: (params) => params.value || "—",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 220,
    },
  ];

  if (!entityId) {
    return <div className="mt-6 text-[#797979]">No entity selected.</div>;
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-3 text-[16px] mb-4">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`font-semibold border-[1px] border-[#797979] px-4 py-2 rounded-[20px] hover:text-white hover:bg-[#34B2C0] hover:border-[#34B2C0] transition-all ${
              filter === item.key
                ? "text-white bg-[#34B2C0] border-[#34B2C0]"
                : "text-[#797979]"
            }`}
            onClick={() => {
              setFilter(item.key);
              setPageState((old) => ({ ...old, page: 1 }));
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {pageState.isLoading && pageState.data.length === 0 ? (
        <LoadingCircle />
      ) : (
        <Table
          data={pageState.data}
          columns={columns}
          isLoading={pageState.isLoading}
          onPageSizeChange={(newPageSize) =>
            setPageState((old) => ({ ...old, pageSize: newPageSize, page: 1 }))
          }
          onPageChange={(newPage) => setPageState((old) => ({ ...old, page: newPage + 1 }))}
          disableFilters={true}
          disableColumnSelector={true}
          disableDensitySelector={true}
          checkboxSelection={false}
          removeActionButton={true}
          removeSearch={true}
          page={pageState.page - 1}
          pageSize={pageState.pageSize}
          rowCount={pageState.total}
          hidePagination={false}
        />
      )}
    </div>
  );
};

export default ActivityLogs;
